import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { ViewerAsset } from '@models/viewer-asset';
import { ButtonComponent } from '@shared/button/button.component';
import { MarkdownService } from '@shared/markdown.service';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';

import { AudioPlayerComponent } from '../../audio-player/audio-player.component';
import { ViewerService } from '../viewer.service';

@Component({
  selector: 'zbp-viewer-audio-player',
  templateUrl: './viewer-audio-player.component.html',
  styleUrls: ['./viewer-audio-player.component.scss'],
  imports: [CommonModule, ButtonComponent, AudioPlayerComponent]
})
export class ViewerAudioPlayerComponent implements OnInit, OnChanges {
  @Input() assetIds: string[] = [];

  @Input() assetList: ViewerAsset[] = [];

  protected readonly zbpIconNames = zbpIconNames;

  private assetIndex: number = -1;
  private assets: ViewerAsset[] = [];
  previousAssets: ViewerAsset[] = [];
  assetName: string = '';
  sameSetOfAudioIds: boolean = false;
  viewerAssets: ViewerAsset[] = [];
  transcript: string = '';  // New property for text alternative
  showTranscript: boolean = false;  // Toggle for expanded visibility

  constructor(
    private markdown: MarkdownService,
    private viewerService: ViewerService) { }

  get audioFile(): string {
    return this.asset
      ? this.viewerService.getAssetFilenameUrl(this.asset, 'media', this.asset.language)
      : null;
  }

  get asset(): ViewerAsset {
    const asset = this.assetIndex !== -1 ? this.assets[this.assetIndex] : null;
    this.assetName = asset != null ? this.markdown.renderInline(asset.name) : '';
    if (asset) {
      this.transcript = asset.transcript ? this.markdown.render(asset.transcript) : this.assetName;
    }
    return asset;
  }

  get hasMultiple(): boolean {
    return this.assets?.length > 1;
  }

  get hasPrevious(): boolean {
    return this.hasMultiple && this.assetIndex > 0;
  }

  get hasNext(): boolean {
    return this.hasMultiple && (this.assets.length - this.assetIndex > 1);
  }

  get prevAudioLabel(): string {
    return this.hasPrevious ? this.assets[this.assetIndex - 1].name : '';
  }

  get nextAudioLabel(): string {
    return this.hasNext ? this.assets[this.assetIndex + 1].name : '';
  }

  ngOnInit(): void {
    this.setAssets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.assetIds) {
      this.setAssets();
    }
  }

  setAssets(): void {
    const filteredAssets = this.assetList?.filter(asset => asset.assetId !== ''
      && this.assetIds.includes(asset.assetId)) || [];

    if (filteredAssets.length > 0) {
      this.assets = filteredAssets;
      this.sameSetOfAudioIds = this.previousAssets != null
        && this.previousAssets.length > 0
        && this.previousAssets.toString() === filteredAssets.toString();
      this.previousAssets = filteredAssets;
      this.assetIndex = !this.sameSetOfAudioIds ? 0 : this.assetIndex;
    } else {
      this.assets = [];
      this.assetIndex = -1;
      this.previousAssets = [];
    }
  }

  next(): void {
    if (this.hasNext) {
      this.assetIndex++;
      // Update transcript for new asset
      this.updateTranscript();
    }
  }

  previous(): void {
    if (this.hasPrevious) {
      this.assetIndex--;
      // Update transcript for new asset
      this.updateTranscript();
    }
  }

  private updateTranscript(): void {
    if (this.asset) {
      this.transcript = this.asset.transcript ? this.markdown.render(this.asset.transcript) : this.assetName;
    }
  }

  toggleTranscript(): void {
    this.showTranscript = !this.showTranscript;
  }
}
